import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Input } from '@ifixit/ui/components/input';
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from '@ifixit/ui/components/select';
import { Button } from '@ifixit/ui/components/button';
import { cn } from '@ifixit/ui/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { GuideEditFields } from './guide_patch';
import { nextAutoTitle, type TitleAutogen, titleIsAuto } from './guide_title';
import { type IntroVideo, parseYouTubeId, watchUrl } from './intro_video_token';
import {
   seedVideoThumbnail,
   useHostedVideo,
   useTopicExists,
   useTopicImage,
} from './use_guide_edit_api';
import { Field } from 'Shared/editor/field';
import { PrimaryImageField, RemoveMediaButton } from 'Shared/editor/primary_image_field';
import { suggestTopics } from 'Shared/editor/suggest_api';
import { SuggestInput } from 'Shared/editor/SuggestInput';
import type { GuideTypeProps } from './types';
import { WikitextField, type WikitextEditor } from 'Shared/editor/wikitext_field';

/**
 * The Introduction tab: what the guide is about and the intro wikitext,
 * edited visually through Tiptap with a source-mode toggle (`?plaintext`
 * starts in source mode).
 */
export function IntroTab({
   fields,
   onChange,
   introEditor,
   guideType,
   isVideoGuide,
   canSetVideoGuideMode,
   titleAutogen,
   onImageProcessingChange,
}: {
   fields: GuideEditFields;
   onChange: (fields: GuideEditFields) => void;
   introEditor: WikitextEditor;
   guideType: GuideTypeProps | null;
   /** Whether the current guide's steps use the selected video timestamps. */
   isVideoGuide: boolean;
   canSetVideoGuideMode: boolean;
   titleAutogen: TitleAutogen;
   /** Blocks the guide save while a main-image crop is producing its copy. */
   onImageProcessingChange?: (isProcessing: boolean) => void;
}) {
   // v1's auto-title, extended to editing: while the title still matches
   // its own auto-generation (a fresh guide, or one nobody hand-titled),
   // identity edits keep regenerating it. A hand-written title is never
   // touched.
   const changeIdentity = (next: GuideEditFields) => {
      const tracking =
         titleAutogen.enabled &&
         titleIsAuto(titleAutogen, {
            title: fields.title,
            type: fields.type,
            device: fields.category,
            subject: fields.subject,
         });
      onChange(
         tracking
            ? {
                 ...next,
                 title: nextAutoTitle(titleAutogen, fields.title, {
                    type: next.type,
                    device: next.category,
                    subject: next.subject,
                 }),
              }
            : next
      );
   };

   // The device whose thumbnail we show — the last committed device, not
   // every keystroke, so the image isn't re-fetched while typing.
   const [settledDevice, setSettledDevice] = useState(fields.category);
   const deviceImage = useTopicImage(settledDevice);
   // Flag a device that doesn't match a known category once the user clicks
   // off it, mirroring the tools/parts "not in our catalog" treatment. Keyed
   // off the settled (committed) value, not every keystroke, so the warning
   // only appears after the field is left, and the query's loading gap keeps
   // it from flashing while the check is in flight.
   const deviceExists = useTopicExists(settledDevice);
   const deviceUnmatched = settledDevice.trim() !== '' && deviceExists.data === false;

   return (
      // On phones one column: identity fields, image, video, editor. From md
      // up the image moves into a narrow right column beside the fields (so
      // the tall preview box doesn't push the editor below the fold) while
      // the video stays in the left column under the fields; the editor
      // keeps the full card width underneath.
      <div className="flex flex-col gap-6">
         <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_20rem] md:items-start">
            <div className="flex flex-col gap-6">
               <Field label={_js('Device')} hint={_js('What device are you working on?')}>
                  <SuggestInput
                     queryKey="guide-topic-suggest"
                     className="w-full"
                     value={fields.category}
                     onValueChange={category => changeIdentity({ ...fields, category })}
                     onSelect={suggestion => {
                        changeIdentity({ ...fields, category: suggestion.label });
                        setSettledDevice(suggestion.label);
                     }}
                     onBlur={() => setSettledDevice(fields.category)}
                     fetchSuggestions={suggestTopics}
                  />
                  {deviceUnmatched && (
                     <span className="mt-1 flex items-center gap-1.5 text-xs text-amber-600">
                        <FaIcon name="triangle-exclamation" className="size-3.5" aria-hidden />
                        {_js("This device doesn't exist in our catalog yet.")}
                     </span>
                  )}
               </Field>

               {guideType && (
                  <Field
                     label={_js('What type of guide is this?')}
                     hint={
                        guideType.options.find(option => option.value === fields.type)
                           ?.description ?? _js('The kind of procedure this guide explains.')
                     }
                  >
                     <Select
                        value={fields.type}
                        onValueChange={type => changeIdentity({ ...fields, type })}
                     >
                        <SelectTrigger className="w-full">
                           <SelectValue placeholder={_js('Choose a type')} />
                        </SelectTrigger>
                        <SelectContent>
                           {guideType.options.map(option => (
                              <SelectItem key={option.value} value={option.value}>
                                 {option.label}
                              </SelectItem>
                           ))}
                        </SelectContent>
                     </Select>
                  </Field>
               )}

               <SubjectField
                  guideType={guideType}
                  type={fields.type}
                  value={fields.subject}
                  onChange={subject => changeIdentity({ ...fields, subject })}
               />

               <Field label={_js('Title')}>
                  <Input
                     value={fields.title}
                     onChange={event => onChange({ ...fields, title: event.target.value })}
                  />
               </Field>
            </div>

            {/* Keyed by image id (device URL as the no-image fallback) so a
                change remounts and retries after a failed load. Auto-placement
                puts the image in the right column and wraps the video back to
                the left one, under the fields. */}
            <PrimaryImageField
               key={fields.image?.imageid ?? deviceImage.data ?? 'none'}
               image={fields.image}
               onChange={image => onChange({ ...fields, image })}
               accepts="guide_main_image"
               fallbackUrl={deviceImage.data ?? null}
               ariaLabel={_js('Guide image')}
               caption={{ image: _js('Main Image'), fallback: _js('Device Photo') }}
               setLabel={_js('Set guide image')}
               onProcessingChange={onImageProcessingChange}
            />

            <VideoField
               video={fields.video}
               isVideoGuide={isVideoGuide}
               canSetVideoGuideMode={canSetVideoGuideMode}
               onChange={video => onChange({ ...fields, video })}
            />
         </div>

         <WikitextField label={_js('Introduction')} editor={introEditor} preset="guide_intro" />
      </div>
   );
}

/**
 * The "thing" field: the part or component the guide is about. Its label and
 * whether it shows at all follow the guide type, like v1 — "What are you
 * replacing/disassembling?" for types with a part, hidden for teardown and
 * technique (whose config prompt is empty). Single-type sites have no per-type
 * data here (no selector), so they keep v1's replacement-style prompt.
 */
function SubjectField({
   guideType,
   type,
   value,
   onChange,
}: {
   guideType: GuideTypeProps | null;
   type: string;
   value: string;
   onChange: (subject: string) => void;
}) {
   const option = guideType?.options.find(choice => choice.value === type);
   // With a selector, the type's prompt is the label (null → hide the field);
   // without one, fall back to the replacement-style prompt (always shown).
   const label = option ? option.thingPrompt : _js('What part are you replacing?');
   if (label === null) {
      return null;
   }
   const hint =
      option && !option.thingRequired
         ? _js('Optional. The part or component the guide works on.')
         : _js('Name the specific part or component, like "Battery".');
   return (
      <Field label={label} hint={hint}>
         <Input value={value} onChange={event => onChange(event.target.value)} />
      </Field>
   );
}

/**
 * The guide's video and how it's used: picked through the Media Manager
 * (a video uploaded to this platform, or a YouTube embed). A YouTube video
 * additionally chooses between "Introduction video" (plays at the top) and
 * "Video guide" (steps each play a segment, which unlocks the timestamp
 * editor on the Steps tab). Backed by the managed wikitext token
 * (intro_video_token.ts); the save composes it into the introduction.
 */
function VideoField({
   video,
   isVideoGuide,
   canSetVideoGuideMode,
   onChange,
}: {
   video: IntroVideo | null;
   isVideoGuide: boolean;
   /**
    * Admin/moderator: may switch between introduction video and video
    * guide. Others see the radios disabled on an existing video guide (so
    * the state is visible), and not at all otherwise.
    */
   canSetVideoGuideMode: boolean;
   onChange: (video: IntroVideo | null) => void;
}) {
   const queryClient = useQueryClient();
   // Non-null when the last pick wasn't usable (e.g. a Vimeo embed).
   const [pickError, setPickError] = useState<string | null>(null);
   // A video guide's video is what its steps play offsets of, so changing
   // or removing it is the same power as the mode radios: replacing it with
   // a hosted video (or clearing it) would end video-guide mode without the
   // privilege the radios gate.
   const lockedVideoGuide =
      !canSetVideoGuideMode &&
      (isVideoGuide || (video?.type === 'youtube' && video.mode === 'guide'));
   const nativeVideo = useHostedVideo(video?.type === 'native' ? video.videoid : null);
   const thumbnail =
      video === null
         ? null
         : video.type === 'youtube'
           ? `https://i.ytimg.com/vi/${video.videoId}/mqdefault.jpg`
           : (nativeVideo.data?.thumbnail ?? null);

   const pickVideo = async () => {
      setPickError(null);
      const { openMediaManager } = await import('Shared/MediaManager');
      const item = await openMediaManager({ accepts: 'guide_step_object' });
      if (!item) {
         return;
      }
      if (item.type === 'GuideVideoObject' && item.videoid !== undefined) {
         seedVideoThumbnail(queryClient, item.videoid, item.thumbnail ?? null);
         onChange({ type: 'native', videoid: item.videoid });
         return;
      }
      const videoId = parseYouTubeId(item.view_url ?? '');
      if (videoId !== null) {
         onChange({
            type: 'youtube',
            videoId,
            mode: video?.type === 'youtube' ? video.mode : 'intro',
         });
         return;
      }
      setPickError(_js('Pick an uploaded video or a YouTube embed.'));
   };

   return (
      <div>
         <span className="mb-1 block text-sm font-medium text-gray-950">{_js('Video')}</span>

         {video ? (
            // The x removes the video; changing it is remove-then-add, so
            // no separate Change button. On a locked video guide the x is
            // hidden too, leaving the video fixed for non-moderators.
            <span className="relative inline-block shrink-0">
               <VideoThumbnail video={video} thumbnail={thumbnail} />
               {!lockedVideoGuide && (
                  <RemoveMediaButton
                     label={_js('Remove video')}
                     onClick={() => onChange(null)}
                     className="absolute right-1 top-1"
                  />
               )}
            </span>
         ) : lockedVideoGuide ? null : (
            // Unlike the image, an absent video is the norm, so the empty
            // state is a plain button rather than a tall drop-zone box.
            <Button
               type="button"
               className="button-md button-outline"
               onClick={() => void pickVideo()}
            >
               <FaIcon name="video" className="size-3.5" aria-hidden />
               {_js('Add video')}
            </Button>
         )}
         {pickError ? (
            <p className="mt-1 text-xs text-red-600">{pickError}</p>
         ) : (
            !video && (
               <span className="mt-1 block text-xs text-gray-500">
                  {_js('Optional. An uploaded video or a YouTube embed, shown with this guide.')}
               </span>
            )
         )}

         {/* Mode is a moderator/admin power; everyone else sees the radios
             only on an existing video guide, disabled, so the state isn't
             invisible. */}
         {video?.type === 'youtube' && (canSetVideoGuideMode || video.mode === 'guide') && (
            <fieldset
               className="m-0 mt-2 flex flex-col gap-2 border-0 p-0"
               disabled={!canSetVideoGuideMode}
            >
               <legend className="sr-only">{_js('How should this video be used?')}</legend>
               <ModeOption
                  title={_js('Introduction video')}
                  description={_js('Plays at the top of the guide.')}
                  checked={video.mode === 'intro'}
                  enabled={canSetVideoGuideMode}
                  onSelect={() => onChange({ ...video, mode: 'intro' })}
               />
               <ModeOption
                  title={_js('Video guide')}
                  description={_js(
                     "Each step plays its segment of the video. Set each step's start and end times in the step editor."
                  )}
                  checked={video.mode === 'guide'}
                  enabled={canSetVideoGuideMode}
                  onSelect={() => onChange({ ...video, mode: 'guide' })}
               />
            </fieldset>
         )}
      </div>
   );
}

/**
 * The video's poster frame at a comfortable size. A YouTube video links out
 * to the video itself; a hosted video has no page to link to.
 */
function VideoThumbnail({ video, thumbnail }: { video: IntroVideo; thumbnail: string | null }) {
   const frame = thumbnail ? (
      <img
         src={thumbnail}
         alt=""
         className="aspect-video w-48 rounded-md border border-gray-300 object-cover"
      />
   ) : (
      <span className="flex aspect-video w-48 items-center justify-center rounded-md border border-gray-300 bg-gray-50 text-gray-400">
         <FaIcon name="video" className="size-6" />
      </span>
   );
   if (video.type !== 'youtube') {
      return frame;
   }
   return (
      <a
         href={watchUrl(video.videoId)}
         target="_blank"
         rel="noopener noreferrer"
         aria-label={_js('Watch on YouTube')}
         title={_js('Watch on YouTube')}
         className="block"
      >
         {frame}
      </a>
   );
}

function ModeOption({
   title,
   description,
   checked,
   enabled,
   onSelect,
}: {
   title: string;
   description: string;
   checked: boolean;
   /** Mirrors the fieldset's disabled state, for the greyed-out text. */
   enabled: boolean;
   onSelect: () => void;
}) {
   return (
      <label className={cn('flex items-start gap-2 text-sm', !enabled && 'cursor-not-allowed')}>
         <input
            type="radio"
            name="introVideoMode"
            className="mt-1"
            checked={checked}
            onChange={onSelect}
         />
         <span>
            <span className={cn('font-medium', enabled ? 'text-gray-700' : 'text-gray-400')}>
               {title}
            </span>
            <span className={cn('block text-xs', enabled ? 'text-gray-500' : 'text-gray-400')}>
               {description}
            </span>
         </span>
      </label>
   );
}
